"use client";

import Section from "@/components/Section";
import { Grid2, Paper, Typography } from "@repo/ui";
import { load } from "cheerio";
import { useMemo, useEffect, useState } from "react";

type CommonPageContentsProps = {
  sectionInfo: PageSections;
  data: {
    description: string;
  };
};

// Use Cheerio for server-side HTML parsing
function processHtmlWithTOC(
  htmlString: string,
  activeHash: string
): {
  tableOfContents: string;
  content: string;
} {
  if (!htmlString) {
    return { tableOfContents: "", content: "" };
  }

  const $ = load(htmlString);
  const tocItems: string[] = [];

  $("h2").each((_, h2) => {
    const text = $(h2).text().trim() || "heading";
    const slug = text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
    $(h2).attr("id", slug);
    const isActive = activeHash === `#${slug}`;
    tocItems.push(
      `<li><a class="${isActive ? "active" : ""}" href="#${slug}">${text}</a></li>`
    );
  });

  return {
    tableOfContents: `<ul>${tocItems.join("")}</ul>`,
    content: $.html(),
  };
}

const CommonPageContents = ({ data, sectionInfo }: CommonPageContentsProps) => {
  const [activeHash, setActiveHash] = useState<string>("");

  useEffect(() => {
    // Set initial hash
    setActiveHash(window.location.hash);

    // Update hash on scroll
    const handleScroll = () => {
      const headings = document.querySelectorAll("h2");
      let currentHash = "";

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 100) {
          // 100px offset from top
          currentHash = `#${heading.id}`;
        }
      });

      if (currentHash && currentHash !== activeHash) {
        setActiveHash(currentHash);
        window.history.replaceState(null, "", currentHash);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeHash]);

  const { tableOfContents, content } = useMemo(
    () => processHtmlWithTOC(data.description, activeHash),
    [data.description, activeHash]
  );

  return (
    <Section id={sectionInfo.sectionId} className={sectionInfo.sectionClass}>
      <Grid2 container spacing={5}>
        <Grid2 item size={{ xs: 1, sm: 3 }} className="relative">
          <div className="sticky top-16">
            <Paper
              className="p-3 table-of-contents [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_li]:mb-2 [&_li.active]:bg-gray-100 [&_li.active]:rounded [&_li.active]:pl-2 [&_a]:text-gray-700 [&_a:hover]:text-blue-600 [&_li.active_a]:text-blue-600 [&_li.active_a]:font-medium"
              dangerouslySetInnerHTML={{ __html: tableOfContents }}
            />
          </div>
        </Grid2>
        <Grid2 item size={{ xs: 1, sm: 9 }}>
          <Paper className="p-4">
            <Typography
              render={<div />}
              className="[&_table]:my-4 [&_table]:border-collapse [&_table]:border [&_table]:w-full [&_th]:bg-gray-100 [&_td]:border [&_td]:p-4 [&_td_p]:!mb-0"
            >
              {content}
            </Typography>
          </Paper>
        </Grid2>
      </Grid2>
    </Section>
  );
};

export default CommonPageContents;
